import { Link } from "react-router-dom";
import { ArrowRight, Calculator, BookOpen, FlaskConical, Factory, Wrench } from "lucide-react";
import { IMAGES } from "../data/images";
import { Seo } from "../components/site/Seo";
import { SectionLabel } from "../components/site/SectionLabel";
import { CTASection } from "../components/site/CTASection";
import { NewsletterForm } from "../components/site/NewsletterForm";
import { AUDIENCES, ENGAGEMENTS, ADVISORY_ICONS } from "../data/site";
import { ADVISORY } from "../data/advisory";
import { EXPERTISE } from "../data/expertise";
import { INSIGHTS } from "../data/insights";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function Home() {
  const latest = INSIGHTS.slice(0, 3);

  return (
    <div>
      <Seo
        title="Polyurethane & Silicone Consulting | Technical Intelligence | MATRIVON"
        description="Independent technical and commercial advisory for polyurethane systems, silicone elastomers, specialty chemicals and industrial materials — with engineering calculators and market intelligence."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "MATRIVON",
          url: "https://matrivon.com",
          description: "Independent commercial and industrial advisory connecting commercial strategy, international markets and industrial expertise.",
          founder: { "@type": "Person", name: "Sönmez Işık", jobTitle: "Founder & Principal Advisor" },
          knowsAbout: ["Polyurethane", "Silicone elastomers", "Specialty chemicals", "Market entry", "Commercial strategy", "Distribution strategy"],
        }}
      />
      {/* Hero — value + formulation cards visible immediately */}
      <section className="relative overflow-hidden bg-brand-ink text-white">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-brand-ink/95 to-brand-ink/80" />
          <div className="absolute inset-0 grid-paper-dark opacity-25" />
        </div>
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-[1.02fr_0.98fr] lg:px-8 lg:py-20">
          {/* Left: message */}
          <div className="animate-rise">
            <SectionLabel light>PU &amp; Silicone Formulation · Advisory · Technical Intelligence</SectionLabel>
            <h1 className="font-heading text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl">
              Expert Polyurethane &amp; Silicone{" "}
              <span className="text-brand-red">formulation support</span> — on demand.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/75">
              Develop, optimise and troubleshoot PU systems and silicone compounds — without hiring a
              full-time specialist. Plus free engineering calculators and market intelligence.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-advisory-btn"
                className="inline-flex items-center gap-2 bg-brand-red px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:brightness-110"
              >
                Discuss your formulation challenge <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/technical-hub/calculators"
                data-testid="hero-technical-btn"
                className="inline-flex items-center gap-2 border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Open PU Calculators
              </Link>
            </div>
            {/* Quick tools */}
            <div className="mt-8">
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/40">Popular tools</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { l: "NCO/OH Calculator", to: "/technical-hub/calculators/nco-oh-ratio" },
                  { l: "Isocyanate Index", to: "/technical-hub/calculators/isocyanate-index" },
                  { l: "PU Knowledge", to: "/technical-hub/polyurethane" },
                  { l: "Silicone Knowledge", to: "/technical-hub/silicones" },
                ].map((t) => (
                  <Link key={t.to} to={t.to} className="border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:border-brand-red hover:text-white">
                    {t.l}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Right: formulation & compounding cards */}
          <div className="grid gap-4 animate-rise" style={{ animationDelay: "0.1s" }}>
            <div data-testid="hero-pu-card" className="border border-white/10 bg-white p-6 text-brand-ink shadow-xl">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center bg-brand-blue text-white"><Wrench className="h-5 w-5" strokeWidth={1.5} /></span>
                <h3 className="font-heading text-lg font-bold">Polyurethane systems support</h3>
              </div>
              <ul className="mt-4 grid grid-cols-1 gap-1.5 text-[14px] text-brand-ink/85 sm:grid-cols-2">
                {["Sandwich panels","Cold-cure & flexible foam","Refrigeration & freezers","Solar heater insulation","Troubleshooting & optimisation"].map((t) => (
                  <li key={t} className="flex gap-2"><ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-red" />{t}</li>
                ))}
              </ul>
              <Link to="/advisory/formulation-support" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5">
                Get PU formulation help <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div data-testid="hero-silicone-card" className="border border-white/10 bg-white p-6 text-brand-ink shadow-xl">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center bg-brand-red text-white"><Factory className="h-5 w-5" strokeWidth={1.5} /></span>
                <h3 className="font-heading text-lg font-bold">Silicone in-house compounding</h3>
              </div>
              <ul className="mt-4 grid grid-cols-1 gap-1.5 text-[14px] text-brand-ink/85 sm:grid-cols-2">
                {["When compounding pays","HCR architecture & gum","Fillers, process aids & cure","Hardness targeting","Reduce cost & gain control"].map((t) => (
                  <li key={t} className="flex gap-2"><ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-red" />{t}</li>
                ))}
              </ul>
              <Link to="/advisory/formulation-support" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5">
                Set up in-house compounding <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning */}
      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <SectionLabel>Positioning</SectionLabel>
              <h2 className="font-heading text-3xl font-bold leading-tight tracking-tight text-brand-ink lg:text-4xl">
                Where commercial strategy meets industrial reality.
              </h2>
            </div>
            <div className="space-y-5 text-[17px] leading-relaxed text-brand-ink/80">
              <p>
                Business decisions in industrial markets rarely fit neatly into a strategy
                presentation. Market attractiveness, customer needs, technology, product performance,
                distribution, pricing, competitors and execution all interact.
              </p>
              <p>
                MATRIVON works at this intersection — combining commercial analysis with practical
                industrial understanding to help companies assess opportunities more realistically and
                turn strategy into executable decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core competencies — PU & Silicone emphasis */}
      <section className="border-b border-brand-line">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <SectionLabel>Core competencies</SectionLabel>
              <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-brand-ink lg:text-4xl">
                Deep expertise in Polyurethane & Silicones.
              </h2>
              <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-brand-muted">
                MATRIVON's technical foundation is strongest in two industrial platforms — combining
                formulation understanding with commercial and market intelligence.
              </p>
            </div>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {/* Polyurethane */}
            <div data-testid="core-polyurethane" className="flex flex-col border border-brand-line bg-white p-8 transition-colors hover:border-brand-blue">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center bg-brand-blue text-white"><FlaskConical className="h-6 w-6" strokeWidth={1.5} /></span>
                <h3 className="font-heading text-2xl font-bold text-brand-ink">Polyurethane</h3>
              </div>
              <p className="mt-4 flex-1 text-[15px] leading-relaxed text-brand-muted">
                PU systems, raw materials and formulation logic — foams, elastomers, coatings and
                adhesives — with equivalent weight, NCO/OH and index calculators.
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <Link to="/expertise/polyurethane" className="border border-brand-line px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:border-brand-blue hover:text-brand-blue">Expertise</Link>
                <Link to="/technical-hub/polyurethane" className="border border-brand-line px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:border-brand-blue hover:text-brand-blue">Knowledge Centre</Link>
                <Link to="/technical-hub/calculators" className="border border-brand-line px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:border-brand-blue hover:text-brand-blue">Calculators</Link>
                <Link to="/advisory/industrial-advisory" className="border border-brand-line px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:border-brand-blue hover:text-brand-blue">Advisory</Link>
              </div>
            </div>

            {/* Silicone */}
            <div data-testid="core-silicone" className="flex flex-col border border-brand-line bg-white p-8 transition-colors hover:border-brand-blue">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center bg-brand-red text-white"><FlaskConical className="h-6 w-6" strokeWidth={1.5} /></span>
                <h3 className="font-heading text-2xl font-bold text-brand-ink">Silicone Elastomers</h3>
              </div>
              <p className="mt-4 flex-1 text-[15px] leading-relaxed text-brand-muted">
                HCR, LSR and RTV systems, compounding architecture, reinforcing fillers and cure
                systems — teaching how to think about formulations, not just recipes.
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <Link to="/expertise/silicones" className="border border-brand-line px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:border-brand-blue hover:text-brand-blue">Expertise</Link>
                <Link to="/technical-hub/silicones" className="border border-brand-line px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:border-brand-blue hover:text-brand-blue">Knowledge Centre</Link>
                <Link to="/technical-hub/resources" className="border border-brand-line px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:border-brand-blue hover:text-brand-blue">Resources</Link>
                <Link to="/advisory/industrial-advisory" className="border border-brand-line px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:border-brand-blue hover:text-brand-blue">Advisory</Link>
              </div>
            </div>
          </div>

          <div className="mt-6 border border-brand-blue/20 bg-brand-blue/5 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[15px] font-medium text-brand-ink">
                Market intelligence: Middle East & Turkey polyurethane and silicone market insights.
              </p>
              <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5">
                Read market intelligence <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who we help */}
      <section className="border-b border-brand-line">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <SectionLabel>Who we work with</SectionLabel>
          <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-brand-ink lg:text-4xl">
            Independent perspective across interconnected audiences.
          </h2>
          <div className="mt-12 grid gap-px border border-brand-line bg-brand-line sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCES.map((a) => (
              <div key={a.title} data-testid="audience-card" className="group bg-white p-8 transition-colors hover:bg-brand-blue/[0.03]">
                <a.icon className="h-7 w-7 text-brand-blue" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-ink">{a.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-muted">{a.body}</p>
              </div>
            ))}
            <div className="flex flex-col justify-center bg-brand-ink p-8 text-white">
              <p className="font-heading text-lg font-medium leading-snug">
                Not sure where your challenge fits?
              </p>
              <Link to="/contact" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:gap-2.5">
                Start a conversation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Advisory areas */}
      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <SectionLabel>Advisory</SectionLabel>
              <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-brand-ink lg:text-4xl">
                Independent advice for complex business decisions.
              </h2>
            </div>
            <Link to="/advisory" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5">
              All advisory services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-12 grid gap-px border border-brand-line bg-brand-line md:grid-cols-2 lg:grid-cols-3">
            {ADVISORY.slice(0, 5).map((a) => {
              const Icon = ADVISORY_ICONS[a.icon];
              return (
                <Link
                  key={a.slug}
                  to={`/advisory/${a.slug}`}
                  data-testid={`advisory-card-${a.slug}`}
                  className="group bg-white p-8 transition-colors hover:bg-brand-blue/[0.03]"
                >
                  {Icon && <Icon className="h-7 w-7 text-brand-red" strokeWidth={1.5} />}
                  <h3 className="mt-5 font-heading text-lg font-bold text-brand-ink group-hover:text-brand-blue">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-muted">{a.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">
                    Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
            <div className="flex items-center bg-brand-bg p-8">
              <p className="font-heading text-lg leading-snug text-brand-ink/70">
                The engagement depends on the decision — from a focused conversation to a structured
                project.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industrial expertise */}
      <section className="relative overflow-hidden border-b border-brand-line bg-brand-ink text-white">
        <div className="absolute inset-0 grid-paper-dark opacity-30" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <SectionLabel light>Industrial expertise</SectionLabel>
          <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight lg:text-4xl">
            Technical understanding changes the quality of commercial decisions.
          </h2>
          <div className="mt-12 flex flex-wrap gap-3">
            {EXPERTISE.map((e) => (
              <Link
                key={e.slug}
                to={`/expertise/${e.slug}`}
                data-testid={`expertise-chip-${e.slug}`}
                className="border border-white/15 bg-white/5 px-5 py-3 text-sm font-medium text-white transition-colors hover:border-brand-red hover:bg-white/10"
              >
                {e.title}
              </Link>
            ))}
          </div>
          <Link to="/expertise" className="mt-10 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:gap-2.5">
            Explore industry expertise <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Founder */}
      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-2 lg:px-8">
          <div className="relative min-h-[320px] overflow-hidden border border-brand-line">
            <img src={IMAGES.founder} alt="Polyurethane insulation sandwich panels" className="h-full w-full object-cover" />
          </div>
          <div>
            <SectionLabel>Founder experience</SectionLabel>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-ink lg:text-4xl">
              Built on real industrial and commercial experience.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-brand-ink/80">
              MATRIVON was founded by <strong>Sönmez Işık</strong>, a chemical engineer and
              international commercial professional whose career has combined production, R&D, technical
              sales, market development, business management and strategic projects across the
              polyurethane and silicone industries.
            </p>
            <p className="mt-4 text-[17px] leading-relaxed text-brand-ink/80">
              Working across Turkey, the Middle East, North Africa and CIS markets shapes MATRIVON's
              approach: <em>understand the technology, understand the market, then assess what makes
              commercial sense.</em>
            </p>
            <Link to="/about" className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5">
              Meet the Founder <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Technical intelligence */}
      <section className="border-b border-brand-line">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <SectionLabel>Technical intelligence</SectionLabel>
          <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-brand-ink lg:text-4xl">
            Practical knowledge for industrial professionals.
          </h2>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            <Link to="/technical-hub/calculators" data-testid="ti-calculators" className="group border border-brand-line bg-white p-8 transition-colors hover:border-brand-blue">
              <Calculator className="h-7 w-7 text-brand-blue" strokeWidth={1.5} />
              <h3 className="mt-5 font-heading text-lg font-bold text-brand-ink">PU Calculators</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                Equivalent weight, NCO/OH ratio and isocyanate index tools for engineers and
                formulators.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">
                Open calculators <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
            <Link to="/technical-hub/polyurethane" data-testid="ti-pu" className="group border border-brand-line bg-white p-8 transition-colors hover:border-brand-blue">
              <BookOpen className="h-7 w-7 text-brand-blue" strokeWidth={1.5} />
              <h3 className="mt-5 font-heading text-lg font-bold text-brand-ink">Polyurethane Knowledge Centre</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                Formulation logic, raw materials, equivalent weights and application development.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">
                Explore polyurethane <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
            <Link to="/technical-hub/silicones" data-testid="ti-silicone" className="group border border-brand-line bg-white p-8 transition-colors hover:border-brand-blue">
              <FlaskConical className="h-7 w-7 text-brand-blue" strokeWidth={1.5} />
              <h3 className="mt-5 font-heading text-lg font-bold text-brand-ink">Silicone Knowledge Centre</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                HCR, LSR and RTV systems, compounding principles and formulation architecture.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">
                Explore silicones <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Insights */}
      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <SectionLabel>Insights</SectionLabel>
              <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-ink lg:text-4xl">
                Independent thinking on markets, industry and technology.
              </h2>
            </div>
            <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5">
              View all insights <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Link
            to="/market-intelligence/global-silicone-industry-2026"
            data-testid="home-featured-global-silicone"
            className="group mt-12 grid overflow-hidden border border-brand-line bg-white transition-colors hover:border-brand-blue/50 lg:grid-cols-[1.3fr_1fr]"
          >
            <div className="aspect-[16/9] w-full overflow-hidden bg-brand-bg lg:aspect-auto">
              <img src="/global-silicone-industry-value-chain-2026.webp" alt="Global silicone industry value chain from silicon metal to specialty silicone applications" width={1200} height={630} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="flex flex-col justify-center p-8 lg:p-10">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-red">Market Intelligence · Silicone Industry</span>
              <h3 className="mt-3 font-heading text-xl font-bold leading-tight text-brand-ink group-hover:text-brand-blue lg:text-2xl">Beyond the "Top Silicone Companies" Lists: The Global Silicone Industry in 2026</h3>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">Read the analysis <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </div>
          </Link>
          <div className="mt-12 grid gap-px border border-brand-line bg-brand-line md:grid-cols-3">
            {latest.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group flex flex-col bg-white p-8 transition-colors hover:bg-brand-blue/[0.03]">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-red">{a.category}</span>
                <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-brand-ink group-hover:text-brand-blue">{a.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-muted">{a.excerpt}</p>
                <span className="mt-5 text-xs text-brand-muted">{fmtDate(a.published)} · {a.readMins} min read</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement + Resources + Newsletter */}
      <section className="border-b border-brand-line">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <SectionLabel>How MATRIVON works</SectionLabel>
          <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-brand-ink lg:text-4xl">
            The engagement depends on the decision.
          </h2>
          <div className="mt-12 grid gap-px border border-brand-line bg-brand-line md:grid-cols-3">
            {ENGAGEMENTS.map((e) => (
              <div key={e.title} className="bg-white p-8">
                <e.icon className="h-7 w-7 text-brand-blue" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-ink">{e.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-muted">{e.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid gap-6 border border-brand-line bg-brand-ink p-8 text-white lg:grid-cols-[1.5fr_1fr] lg:items-center lg:p-12">
            <div>
              <h3 className="font-heading text-2xl font-bold">Subscribe to MATRIVON Insights</h3>
              <p className="mt-2 max-w-lg text-sm text-white/60">
                New tool reviews, market intelligence and technical resources — no intrusive noise.
              </p>
            </div>
            <NewsletterForm source="home" dark />
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
